import {Component, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {MatSnackBar} from '@angular/material/snack-bar';

@Component({
  selector: 'app-superadmin-rebate-detail',
  templateUrl: './superadmin-rebate-detail.component.html',
  styleUrls: ['./superadmin-rebate-detail.component.scss'],
})
export class SuperadminRebateDetailComponent implements OnInit {
  public reward: any;
  public rewards: any[] = [];
  public saving = false;

  public form = this.formBuilder.group({
    title: ['', Validators.required],
    linkedreward: ['', Validators.required],
    transactionRegExp: ['', Validators.required],
    redeemDetails: [{}],
    visibleExpression: [{}],
    schedule: [{}],
  });

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadRewards();
    this.loadRebate();
  }

  async loadRewards(): Promise<void> {
    try {
      this.rewards = await this.rewardsAdminService.getRewards('', 0, 100);
    } catch (error) {
      console.error('Error loading rewards', error);
    }
  }

  parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }

  async loadRebate(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id') || 'new';
    if (id === 'new') {
      return;
    }
    try {
      this.reward = await this.rewardsAdminService.getPurchasedRewardById(id);
      this.form.patchValue({
        title: this.reward.title,
        linkedreward: this.reward.rewardId,
        transactionRegExp: this.reward.transactionRegExp,
        redeemDetails: this.parseJsonValue(this.reward.redeemDetails),
        visibleExpression: this.parseJsonValue(this.reward.visibleExpression),
        schedule: this.parseJsonValue(this.reward.schedule),
      });
    } catch (error) {
      console.error('Error loading rebate', error);
    }
  }

  toJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const payload: any = {
      title: this.form.get('title')?.value,
      rewardId: this.form.get('linkedreward')?.value,
      transactionRegExp: this.form.get('transactionRegExp')?.value,
      redeemDetails: this.toJsonValue(this.form.get('redeemDetails')?.value) || '',
      visibleExpression: this.toJsonValue(this.form.get('visibleExpression')?.value) || '',
      schedule: this.toJsonValue(this.form.get('schedule')?.value) || '',
    };
    try {
      if (this.reward) {
        payload.id = this.reward.id;
        await this.rewardsAdminService.putPurchasedRewardById(payload.id, payload);
        this.snackBar.open('CLO successfully updated', 'Close', { duration: 2000 });
      } else {
        await this.rewardsAdminService.postPurchasedReward(payload);
        this.snackBar.open('CLO successfully created', 'Close', { duration: 2000 });
      }
      this.router.navigate(['/superadmin/rewards/rebates']).then();
    } catch (error) {
      console.error('Error saving rebate', error);
    } finally {
      this.saving = false;
    }
  }

  async delete(): Promise<void> {
    if (!this.reward) {
      return;
    }
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this CLO?');
    if (!confirm) {
      return;
    }
    try {
      await this.rewardsAdminService.deletePurchasedRewardById(this.reward.id);
      this.snackBar.open('CLO successfully deleted', 'Close', { duration: 2000 });
      this.router.navigate(['/superadmin/rewards/rebates']).then();
    } catch (error) {
      console.error('Error deleting rebate', error);
    }
  }

  cancel(): void {
    this.router.navigate(['/superadmin/rewards/rebates']).then();
  }
}
